import type {
  ChecklistItemPublic,
  OffboardRequest,
  OffboardingPreviewResponse,
  OffboardingRecordAction,
  OffboardingRecordPreview,
} from '@accounts/shared';

export type OffboardingChoices = Readonly<Record<string, OffboardingRecordAction>>;

export function appDeactivationState(
  item: ChecklistItemPublic,
): 'confirmed' | 'overdue' | 'pending' {
  if (
    item.status === 'done' ||
    (item.deprovisioning !== null && item.deprovisioning.confirmedAt !== null)
  ) {
    return 'confirmed';
  }
  return item.deprovisioning?.overdue === true ? 'overdue' : 'pending';
}

export function selectedOffboardingAction(
  item: OffboardingRecordPreview,
  choices: OffboardingChoices,
): OffboardingRecordAction {
  return choices[item.record.id] ?? item.defaultChoice;
}

export function buildOffboardRequest(
  preview: OffboardingPreviewResponse,
  choices: OffboardingChoices,
): OffboardRequest {
  const records = [...preview.sharedLogins, ...preview.personalLogins];
  return {
    choices: Object.fromEntries(
      records.map((item) => [item.record.id, selectedOffboardingAction(item, choices)]),
    ),
  };
}

export function offboardingCounts(
  preview: OffboardingPreviewResponse,
  choices: OffboardingChoices,
): Record<OffboardingRecordAction | 'app_deactivation', number> {
  const counts: Record<OffboardingRecordAction | 'app_deactivation', number> = {
    remove: 0,
    remove_vm_access: 0,
    rotate_shared_password: 0,
    remove_from_shared_mailbox: 0,
    app_deactivation: preview.suiteApps.length,
  };
  for (const item of [...preview.sharedLogins, ...preview.personalLogins]) {
    counts[selectedOffboardingAction(item, choices)] += 1;
  }
  return counts;
}

/**
 * Shared logins set to Remove account, and how many other people that locks out. Each
 * person counts once, however many of the removed logins they share.
 */
export function removeAccountLockout(
  preview: OffboardingPreviewResponse,
  choices: OffboardingChoices,
): { logins: number; people: number } {
  const removed = preview.sharedLogins.filter(
    (item) =>
      item.record.sharedWith.length > 0 && selectedOffboardingAction(item, choices) === 'remove',
  );
  const people = new Set(removed.flatMap((item) => item.record.sharedWith.map((user) => user.id)));
  return { logins: removed.length, people: people.size };
}
